import { KeyboardEvent, ReactElement, ReactNode, useEffect, useId, useRef } from 'react'
import { salesReportIcons } from '../icons'
import * as S from './styles'

interface BottomSheetProps {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}

let scrollLocks = 0
let unlockedOverflow = ''

const lockPageScroll = (): void => {
  if (scrollLocks === 0) unlockedOverflow = document.body.style.overflow
  scrollLocks += 1
  document.body.style.overflow = 'hidden'
}

const unlockPageScroll = (): void => {
  scrollLocks = Math.max(0, scrollLocks - 1)
  if (scrollLocks === 0) document.body.style.overflow = unlockedOverflow
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function BottomSheet({ open, title, onClose, children, footer }: BottomSheetProps): ReactElement | null {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    lockPageScroll()
    closeRef.current?.focus()
    return () => {
      unlockPageScroll()
      opener?.focus()
    }
  }, [open])

  if (!open) return null

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.defaultPrevented) return
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab' || panelRef.current == null) return
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <>
      <S.Overlay data-testid="bottom-sheet-overlay" onClick={onClose} />
      <S.Panel ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown}>
        <S.Handle aria-hidden="true" />
        <S.Top>
          <S.Title id={titleId}>{title}</S.Title>
          <S.Close ref={closeRef} type="button" aria-label="Fechar" onClick={onClose}>
            <img src={salesReportIcons.close} alt="" width={20} height={20} />
          </S.Close>
        </S.Top>
        <S.Body>{children}</S.Body>
        {footer != null && <S.Footer>{footer}</S.Footer>}
      </S.Panel>
    </>
  )
}
